import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction
} from '../components/ui/alert-dialog';
import {
  Lock, LogOut, Users, MessageSquare, CalendarCheck, UserCheck, TrendingUp, Search, CheckCircle2,
  XCircle, Mail, Phone, Download, CalendarDays, Clock, LayoutDashboard, Sparkles, Trash2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'cqoe_admin_token';

const PROFILE_LABELS = { pilier: 'Le Pilier', conciliateur: 'Le Conciliateur', independant: "L'Indépendant", sensible: 'Le Sensible au lien', explorateur: 'Explorateur', batisseur: 'Bâtisseur', relationnel: 'Relationnel', intuitif: 'Intuitif' };
const SOURCE_LABELS = { test: 'Test', formulaire: 'Formulaire', calendly: 'Calendly' };
const STATUS_OPTIONS = [
  { value: 'nouveau', label: 'Nouveau', cls: 'bg-gray-100 text-gray-700' },
  { value: 'contacte', label: 'Contacté', cls: 'bg-blue-100 text-blue-700' },
  { value: 'rdv_programme', label: 'Rendez-vous programmé', cls: 'bg-amber-100 text-amber-700' },
  { value: 'client', label: 'Client', cls: 'bg-teal-100 text-teal-700' },
  { value: 'a_relancer', label: 'À relancer', cls: 'bg-pink-100 text-pink-700' },
];
const statusMeta = (v) => STATUS_OPTIONS.find((s) => s.value === v) || STATUS_OPTIONS[0];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};
const fmtDay = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return iso; }
};
const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};
// ISO -> "YYYY-MM-DDTHH:mm" (local) for datetime-local input
const toInputDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const sameDay = (iso, date) => {
  if (!iso) return false;
  const d = new Date(iso);
  return d.getFullYear() === date.getFullYear() && d.getMonth() === date.getMonth() && d.getDate() === date.getDate();
};

export const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || '');
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');

  const [fiche, setFiche] = useState(null);
  const [ficheOpen, setFicheOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [rdvDraft, setRdvDraft] = useState('');
  const [savingRdv, setSavingRdv] = useState(false);

  const [calDay, setCalDay] = useState(new Date());

  const [selected, setSelected] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(null); // { emails: [], label: '' }
  const [deleting, setDeleting] = useState(false);

  const authHeader = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(''); setAuthed(false); setStats(null); setLeads([]);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/admin/stats`, authHeader()),
        axios.get(`${API}/admin/leads`, authHeader())
      ]);
      setStats(s.data); setLeads(l.data);
    } catch (err) { if (err?.response?.status === 401) logout(); }
  }, [authHeader, logout]);

  useEffect(() => {
    const check = async () => {
      if (!token) { setChecking(false); return; }
      try { await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }); setAuthed(true); }
      catch { localStorage.removeItem(TOKEN_KEY); setToken(''); setAuthed(false); }
      finally { setChecking(false); }
    };
    check();
  }, [token]);

  useEffect(() => { if (authed) loadData(); }, [authed, loadData]);

  const handleLogin = async (e) => {
    e.preventDefault(); setLoginError(''); setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email: email.trim(), password });
      localStorage.setItem(TOKEN_KEY, data.token); setToken(data.token); setAuthed(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setLoginError(typeof detail === 'string' ? detail : 'Connexion impossible. Réessayez.');
    } finally { setLoggingIn(false); }
  };

  const openFiche = async (lead) => {
    setFicheOpen(true); setFiche({ ...lead, loading: true }); setNotesDraft(lead.notes || ''); setRdvDraft(toInputDate(lead.rdvDate));
    try {
      const { data } = await axios.get(`${API}/admin/lead?email=${encodeURIComponent(lead.email)}`, authHeader());
      setFiche(data); setNotesDraft(data.notes || ''); setRdvDraft(toInputDate(data.rdvDate));
    } catch { setFiche((f) => ({ ...f, loading: false })); }
  };

  const updateLead = async (leadEmail, patch) => {
    await axios.patch(`${API}/admin/lead`, { email: leadEmail, ...patch }, authHeader());
    setLeads((prev) => prev.map((l) => (l.email === leadEmail ? { ...l, ...patch } : l)));
    loadData();
  };

  const changeStatus = async (leadEmail, status) => {
    await updateLead(leadEmail, { status });
    setFiche((f) => (f && f.email === leadEmail ? { ...f, status } : f));
  };

  const saveNotes = async () => {
    if (!fiche) return;
    setSavingNotes(true);
    try { await updateLead(fiche.email, { notes: notesDraft }); setFiche((f) => ({ ...f, notes: notesDraft })); }
    finally { setSavingNotes(false); }
  };

  const saveRdv = async () => {
    if (!fiche) return;
    setSavingRdv(true);
    const iso = rdvDraft ? new Date(rdvDraft).toISOString() : '';
    try {
      await updateLead(fiche.email, { rdvDate: iso });
      setFiche((f) => ({ ...f, rdvDate: iso || null }));
    } finally { setSavingRdv(false); }
  };

  // ---- Filtering ----
  const filtered = useMemo(() => leads.filter((l) => {
    if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (sourceFilter !== 'all' && !(l.sources || []).includes(sourceFilter)) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const hit = [l.firstName, l.name, l.email, l.profileName, PROFILE_LABELS[l.profile]]
        .filter(Boolean).some((v) => v.toLowerCase().includes(q));
      if (!hit) return false;
    }
    return true;
  }), [leads, statusFilter, sourceFilter, search]);

  // ---- CSV export ----
  const exportCSV = () => {
    const headers = ['Prénom', 'Nom', 'Email', 'Téléphone', 'Langue', 'Provenance', 'Profil', 'Statut', 'Date RDV', 'Première activité', 'Dernière activité', 'Consentement RGPD'];
    const rows = filtered.map((l) => [
      l.firstName || '', l.name || '', l.email || '', l.phone || '', (l.language || '').toUpperCase(),
      (l.sources || []).map((s) => SOURCE_LABELS[s] || s).join(' / '),
      l.profileName || PROFILE_LABELS[l.profile] || '', statusMeta(l.status).label,
      l.rdvDate ? fmtDate(l.rdvDate) : '', fmtDate(l.firstSeen), fmtDate(l.lastActivity),
      l.consentGiven ? 'Oui' : 'Non',
    ]);
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((r) => r.map(esc).join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---- Selection & deletion ----
  const toggleSelect = (email) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    const emails = confirmDelete.emails;
    setDeleting(true);
    try {
      if (emails.length === 1) {
        await axios.delete(`${API}/admin/lead?email=${encodeURIComponent(emails[0])}`, authHeader());
      } else {
        await axios.post(`${API}/admin/leads/bulk-delete`, { emails }, authHeader());
      }
      setLeads((prev) => prev.filter((l) => !emails.includes(l.email)));
      setSelected(new Set());
      setConfirmDelete(null);
      if (fiche && emails.includes(fiche.email)) setFicheOpen(false);
      loadData();
    } finally { setDeleting(false); }
  };

  // ---- Appointments (calendar) ----
  const appointments = useMemo(() => leads.filter((l) => l.rdvDate).sort((a, b) => a.rdvDate.localeCompare(b.rdvDate)), [leads]);
  const rdvDays = useMemo(() => appointments.map((l) => new Date(l.rdvDate)), [appointments]);
  const dayAppointments = useMemo(() => appointments.filter((l) => sameDay(l.rdvDate, calDay)), [appointments, calDay]);

  if (checking) {
    return <div className="min-h-screen pt-20 flex items-center justify-center bg-gradient-to-br from-teal-50 via-white to-pink-50"><p className="text-gray-500">Chargement...</p></div>;
  }

  // LOGIN
  if (!authed) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-gradient-to-br from-teal-50 via-white to-pink-50 px-4">
        <Card className="border-none shadow-2xl w-full max-w-md" data-testid="admin-login">
          <CardContent className="p-8 sm:p-10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-pink-500 flex items-center justify-center mx-auto mb-4"><Lock size={26} className="text-white" /></div>
              <h1 className="text-2xl font-light text-teal-600">Espace administrateur</h1>
              <p className="text-sm text-gray-500 mt-1">Connectez-vous pour gérer votre activité.</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2"><Label htmlFor="admin-email">Email</Label>
                <Input id="admin-email" type="email" data-testid="admin-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required className="border-gray-300 focus:border-teal-500 focus:ring-teal-500" /></div>
              <div className="space-y-2"><Label htmlFor="admin-password">Mot de passe</Label>
                <Input id="admin-password" type="password" data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required className="border-gray-300 focus:border-teal-500 focus:ring-teal-500" /></div>
              {loginError && <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700" data-testid="admin-login-error">{loginError}</div>}
              <Button type="submit" size="lg" disabled={loggingIn} data-testid="admin-login-button" className="w-full bg-gradient-to-r from-teal-500 to-pink-500 hover:from-teal-600 hover:to-pink-600 text-white py-6 h-auto disabled:opacity-70">{loggingIn ? 'Connexion...' : 'Se connecter'}</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, value, label, color, testid }) => (
    <Card className="border-none shadow-lg"><CardContent className="p-5">
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-full ${color} flex items-center justify-center`}><Icon size={20} /></div>
        <div><p className="text-2xl font-semibold text-gray-800" data-testid={testid}>{value}</p><p className="text-xs text-gray-500">{label}</p></div>
      </div>
    </CardContent></Card>
  );

  const PeriodCard = ({ title, data, accent }) => (
    <Card className="border-none shadow-lg"><CardContent className="p-5">
      <p className="text-xs uppercase tracking-wide text-gray-500 mb-3">{title}</p>
      <div className="grid grid-cols-3 gap-2 text-center">
        {[['Aujourd\'hui', data.today], ['7 jours', data.week], ['Ce mois', data.month]].map(([lbl, val]) => (
          <div key={lbl} className="rounded-xl bg-gray-50 py-3">
            <p className={`text-xl font-semibold ${accent}`}>{val}</p>
            <p className="text-[11px] text-gray-500 mt-0.5">{lbl}</p>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );

  const LeadsTable = ({ rows }) => {
    const allSelected = rows.length > 0 && rows.every((l) => selected.has(l.email));
    const toggleAll = () => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (allSelected) rows.forEach((l) => next.delete(l.email));
        else rows.forEach((l) => next.add(l.email));
        return next;
      });
    };
    return (
    <Card className="border-none shadow-lg">
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead>
            <tr className="text-left text-gray-500 border-b border-gray-100">
              <th className="px-4 py-3 w-10"><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-checkbox" aria-label="Tout sélectionner" /></th>
              <th className="px-4 py-3 font-medium">Contact</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Langue</th>
              <th className="px-4 py-3 font-medium">Provenance</th>
              <th className="px-4 py-3 font-medium">Profil</th>
              <th className="px-4 py-3 font-medium">Statut</th>
              <th className="px-4 py-3 font-medium">RDV</th>
              <th className="px-4 py-3 font-medium">Dernière activité</th>
              <th className="px-4 py-3 font-medium">RGPD</th>
              <th className="px-4 py-3 font-medium text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="11" className="px-4 py-8 text-center text-gray-400">Aucun contact.</td></tr>}
            {rows.map((l) => (
              <tr key={l.email} className={`border-b border-gray-50 hover:bg-teal-50/40 cursor-pointer ${selected.has(l.email) ? 'bg-teal-50/60' : ''}`} data-testid="lead-row" onClick={() => openFiche(l)}>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selected.has(l.email)} onCheckedChange={() => toggleSelect(l.email)} data-testid="lead-select-checkbox" aria-label={`Sélectionner ${l.email}`} />
                </td>
                <td className="px-4 py-3 text-gray-800 font-medium">{l.firstName || l.name || '—'}{l.name && l.firstName && l.name !== l.firstName ? <span className="text-gray-400 font-normal"> · {l.name}</span> : ''}</td>
                <td className="px-4 py-3 text-gray-600">{l.email}</td>
                <td className="px-4 py-3 text-gray-500 uppercase text-xs">{l.language}</td>
                <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{l.sources.map((s) => <Badge key={s} variant="secondary" className="text-xs">{SOURCE_LABELS[s] || s}</Badge>)}</div></td>
                <td className="px-4 py-3">{l.profile ? <span className="inline-block px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 text-xs">{l.profileName || PROFILE_LABELS[l.profile]}</span> : <span className="text-gray-300">—</span>}</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <Select value={l.status} onValueChange={(v) => changeStatus(l.email, v)}>
                    <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{l.rdvDate ? fmtDate(l.rdvDate) : <span className="text-gray-300">—</span>}</td>
                <td className="px-4 py-3 text-gray-500">{fmtDate(l.lastActivity)}</td>
                <td className="px-4 py-3">{l.consentGiven ? <CheckCircle2 size={18} className="text-teal-500" /> : <XCircle size={18} className="text-gray-300" />}</td>
                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid="lead-delete-button" aria-label={`Supprimer ${l.email}`} onClick={() => setConfirmDelete({ emails: [l.email], label: l.firstName || l.name || l.email })}><Trash2 size={16} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
    );
  };

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-teal-50 via-white to-pink-50" data-testid="admin-dashboard">
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl sm:text-3xl font-light text-teal-600">Tableau de bord</h1>
            <Button variant="outline" onClick={logout} data-testid="admin-logout-button" className="border-gray-300 text-gray-600 hover:text-teal-600"><LogOut size={18} className="mr-2" /> Déconnexion</Button>
          </div>

          <Tabs defaultValue="dashboard" className="w-full">
            <TabsList className="mb-6 bg-white/70 shadow-sm">
              <TabsTrigger value="dashboard" data-testid="tab-dashboard"><LayoutDashboard size={16} className="mr-2" /> Tableau de bord</TabsTrigger>
              <TabsTrigger value="leads" data-testid="tab-leads"><Users size={16} className="mr-2" /> Leads</TabsTrigger>
              <TabsTrigger value="calendar" data-testid="tab-calendar"><CalendarDays size={16} className="mr-2" /> Calendrier</TabsTrigger>
            </TabsList>

            {/* DASHBOARD */}
            <TabsContent value="dashboard">
              {stats && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <StatCard icon={Users} value={stats.totalTests} label="Tests réalisés" color="bg-teal-100 text-teal-600" testid="stat-total-tests" />
                    <StatCard icon={MessageSquare} value={stats.totalContacts} label="Messages reçus" color="bg-pink-100 text-pink-600" testid="stat-total-contacts" />
                    <StatCard icon={CalendarCheck} value={stats.rdvCount} label="Rendez-vous" color="bg-amber-100 text-amber-600" testid="stat-rdv" />
                    <StatCard icon={UserCheck} value={stats.newClients} label="Clients" color="bg-teal-100 text-teal-600" testid="stat-clients" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <PeriodCard title="Tests réalisés" data={stats.testsPeriod || { today: 0, week: 0, month: 0 }} accent="text-teal-600" />
                    <PeriodCard title="Messages reçus" data={stats.contactsPeriod || { today: 0, week: 0, month: 0 }} accent="text-pink-600" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
                    <Card className="border-none shadow-lg"><CardContent className="p-5 flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center"><TrendingUp size={20} /></div>
                      <div><p className="text-2xl font-semibold text-gray-800" data-testid="stat-conv-test-rdv">{stats.conversionTestToRdv}%</p><p className="text-xs text-gray-500">Transformation Test → Rendez-vous</p></div>
                    </CardContent></Card>
                    <Card className="border-none shadow-lg"><CardContent className="p-5 flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center"><TrendingUp size={20} /></div>
                      <div><p className="text-2xl font-semibold text-gray-800" data-testid="stat-conv-rdv-client">{stats.conversionRdvToClient}%</p><p className="text-xs text-gray-500">Transformation Rendez-vous → Client</p></div>
                    </CardContent></Card>
                    <Card className="border-none shadow-lg"><CardContent className="p-5 flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center"><Clock size={20} /></div>
                      <div><p className="text-2xl font-semibold text-gray-800" data-testid="stat-upcoming-rdv">{stats.upcomingRdv ?? 0}</p><p className="text-xs text-gray-500">Rendez-vous à venir</p></div>
                    </CardContent></Card>
                  </div>
                </>
              )}
            </TabsContent>

            {/* LEADS */}
            <TabsContent value="leads">
              <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-md">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input data-testid="admin-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (prénom, nom, email, profil)..." className="pl-10 border-gray-300 focus:border-teal-500 focus:ring-teal-500" />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full md:w-[200px] border-gray-300" data-testid="filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="w-full md:w-[180px] border-gray-300" data-testid="filter-source"><SelectValue placeholder="Provenance" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes provenances</SelectItem>
                    <SelectItem value="test">Test</SelectItem>
                    <SelectItem value="formulaire">Formulaire</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={exportCSV} data-testid="export-csv-button" className="bg-teal-600 hover:bg-teal-700 text-white whitespace-nowrap"><Download size={16} className="mr-2" /> Exporter CSV</Button>
              </div>
              <p className="text-xs text-gray-500 mb-3" data-testid="leads-count">{filtered.length} contact{filtered.length > 1 ? 's' : ''} affiché{filtered.length > 1 ? 's' : ''}</p>
              {selected.size > 0 && (
                <div className="flex items-center justify-between bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-3" data-testid="bulk-action-bar">
                  <span className="text-sm text-red-700">{selected.size} sélectionné{selected.size > 1 ? 's' : ''}</span>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} className="text-gray-500">Annuler</Button>
                    <Button size="sm" onClick={() => setConfirmDelete({ emails: Array.from(selected), label: `${selected.size} contact${selected.size > 1 ? 's' : ''}` })} data-testid="bulk-delete-button" className="bg-red-600 hover:bg-red-700 text-white"><Trash2 size={15} className="mr-2" /> Supprimer la sélection</Button>
                  </div>
                </div>
              )}
              <LeadsTable rows={filtered} />
            </TabsContent>

            {/* CALENDAR */}
            <TabsContent value="calendar">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="calendar-view">
                <Card className="border-none shadow-lg">
                  <CardContent className="p-4 flex justify-center">
                    <Calendar
                      mode="single"
                      selected={calDay}
                      onSelect={(d) => d && setCalDay(d)}
                      modifiers={{ hasRdv: rdvDays }}
                      modifiersClassNames={{ hasRdv: 'bg-amber-100 text-amber-700 font-semibold rounded-full' }}
                    />
                  </CardContent>
                </Card>
                <Card className="border-none shadow-lg">
                  <CardContent className="p-5">
                    <h3 className="text-lg font-light text-teal-600 mb-1">Rendez-vous du {fmtDay(calDay.toISOString())}</h3>
                    <p className="text-xs text-gray-500 mb-4">Les jours avec rendez-vous sont surlignés dans le calendrier.</p>
                    <div className="space-y-2" data-testid="day-appointments">
                      {dayAppointments.length === 0 && <p className="text-gray-400 text-sm py-6 text-center">Aucun rendez-vous ce jour-là.</p>}
                      {dayAppointments.map((l) => (
                        <div key={l.email} className="flex items-center justify-between bg-amber-50/60 rounded-xl px-4 py-3 cursor-pointer hover:bg-amber-50" onClick={() => openFiche(l)} data-testid="appointment-item">
                          <div>
                            <p className="text-gray-800 font-medium text-sm">{l.firstName || l.name || l.email}</p>
                            <p className="text-gray-500 text-xs">{l.email}</p>
                          </div>
                          <Badge className="bg-amber-100 text-amber-700"><Clock size={13} className="mr-1" /> {fmtTime(l.rdvDate)}</Badge>
                        </div>
                      ))}
                    </div>

                    {appointments.length > 0 && (
                      <div className="mt-6">
                        <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Tous les prochains rendez-vous</p>
                        <div className="space-y-1">
                          {appointments.filter((l) => new Date(l.rdvDate) >= new Date(new Date().setHours(0, 0, 0, 0))).slice(0, 8).map((l) => (
                            <div key={l.email} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-50 cursor-pointer hover:text-teal-600" onClick={() => openFiche(l)}>
                              <span className="text-gray-700">{l.firstName || l.name || l.email}</span>
                              <span className="text-gray-400 text-xs">{fmtDate(l.rdvDate)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* FICHE */}
      <Dialog open={ficheOpen} onOpenChange={setFicheOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="lead-fiche">
          {fiche && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 flex-wrap">
                  <DialogTitle className="text-xl font-light text-teal-600">{fiche.firstName || fiche.name || 'Contact'}</DialogTitle>
                  <Badge className={statusMeta(fiche.status).cls}>{statusMeta(fiche.status).label}</Badge>
                  {fiche.profile && <Badge variant="secondary" className="text-xs"><Sparkles size={12} className="mr-1" /> {fiche.profileName || PROFILE_LABELS[fiche.profile]}</Badge>}
                </div>
                <DialogDescription className="text-sm text-gray-500">Fiche contact : coordonnées, rendez-vous, statut, notes et historique.</DialogDescription>
              </DialogHeader>

              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-700"><Mail size={16} className="text-gray-400" /> {fiche.email}</div>
                  {fiche.phone && <div className="flex items-center gap-2 text-gray-700"><Phone size={16} className="text-gray-400" /> {fiche.phone}</div>}
                  <div className="text-gray-600">Langue : <span className="uppercase">{fiche.language}</span></div>
                  <div className="text-gray-600">Provenance : {(fiche.sources || []).map((s) => SOURCE_LABELS[s] || s).join(', ') || '—'}</div>
                  <div className="text-gray-600">Premier contact : {fmtDate(fiche.firstSeen)}</div>
                  <div className="text-gray-600">Dernière activité : {fmtDate(fiche.lastActivity)}</div>
                  <div className="text-gray-600 sm:col-span-2">Consentement RGPD : {fiche.consentGiven ? `Oui (${fmtDate(fiche.consentTimestamp)})` : 'Non'}</div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Statut</Label>
                    <Select value={fiche.status} onValueChange={(v) => changeStatus(fiche.email, v)}>
                      <SelectTrigger className="mt-1 w-full" data-testid="fiche-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Date & heure du rendez-vous</Label>
                    <div className="flex gap-2 mt-1">
                      <Input type="datetime-local" value={rdvDraft} onChange={(e) => setRdvDraft(e.target.value)} data-testid="fiche-rdv-input" className="border-gray-300 focus:border-teal-500 focus:ring-teal-500" />
                      <Button size="sm" onClick={saveRdv} disabled={savingRdv} data-testid="fiche-save-rdv" className="bg-amber-500 hover:bg-amber-600 text-white whitespace-nowrap">{savingRdv ? '...' : 'OK'}</Button>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-gray-500">Notes personnelles</Label>
                  <Textarea data-testid="fiche-notes" value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} rows={4} className="mt-1 border-gray-300 focus:border-teal-500 focus:ring-teal-500" placeholder="Ajoutez vos notes sur ce contact..." />
                  <Button size="sm" onClick={saveNotes} disabled={savingNotes} data-testid="fiche-save-notes" className="mt-2 bg-teal-600 hover:bg-teal-700 text-white">{savingNotes ? 'Enregistrement...' : 'Enregistrer les notes'}</Button>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Historique</p>
                  <div className="space-y-2">
                    {(fiche.tests || []).map((t) => (
                      <div key={t.id} className="flex items-center justify-between bg-teal-50/50 rounded-lg px-3 py-2 text-sm">
                        <span className="text-gray-700">Test → <strong>{t.profileName || PROFILE_LABELS[t.profile]}</strong></span>
                        <span className="text-gray-400 text-xs">{fmtDate(t.createdAt)}</span>
                      </div>
                    ))}
                    {(fiche.messages || []).map((m) => (
                      <div key={m.id} className="bg-pink-50/50 rounded-lg px-3 py-2 text-sm">
                        <div className="flex items-center justify-between"><span className="text-gray-700">Message de contact</span><span className="text-gray-400 text-xs">{fmtDate(m.createdAt)}</span></div>
                        <p className="text-gray-600 mt-1 text-xs line-clamp-3">{m.message}</p>
                      </div>
                    ))}
                    {(!fiche.tests || fiche.tests.length === 0) && (!fiche.messages || fiche.messages.length === 0) && <p className="text-gray-400 text-sm">Aucun historique.</p>}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* CONFIRMATION SUPPRESSION */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {confirmDelete?.label} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est définitive. Toutes les données associées (tests, messages de contact, rendez-vous, statut et notes) seront supprimées. Cette opération est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); performDelete(); }} disabled={deleting} data-testid="delete-confirm-button" className="bg-red-600 hover:bg-red-700 text-white">{deleting ? 'Suppression...' : 'Supprimer définitivement'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
